import JSZip from 'jszip';
import type { ConversionResult, DroppedContent, ParserContext } from './types';
import { findAll } from './xmlUtils';
import { parseRels, parseStyles, parseNumbering } from './ooxmlParsers';
import { processBlockChildren } from './processors';

export type { ConversionResult } from './types';

export async function convertDocxToHtml(
    arrayBuffer: ArrayBuffer
): Promise<ConversionResult> {
    const zip = await JSZip.loadAsync(arrayBuffer);
    const warnings: string[] = [];

    const relsFile = zip.file('word/_rels/document.xml.rels');
    const rels = relsFile
        ? parseRels(await relsFile.async('string'))
        : new Map<string, string>();

    const numberingFile = zip.file('word/numbering.xml');
    let numbering = new Map<string, Map<number, string>>();
    if (numberingFile) {
        numbering = parseNumbering(await numberingFile.async('string'));
    } else {
        warnings.push(
            'No numbering.xml found — list formatting may be basic'
        );
    }

    const stylesFile = zip.file('word/styles.xml');
    let headingStyles = new Map<string, number>();
    if (stylesFile) {
        headingStyles = parseStyles(await stylesFile.async('string'));
    } else {
        warnings.push(
            'No styles.xml found — heading detection will use fallback matching'
        );
    }

    const docFile = zip.file('word/document.xml');
    if (!docFile) throw new Error('Invalid DOCX: no word/document.xml found');

    const doc = new DOMParser().parseFromString(
        await docFile.async('string'),
        'application/xml'
    );
    const parseError = doc.querySelector('parsererror');
    if (parseError)
        throw new Error(
            `Malformed document.xml: ${parseError.textContent?.slice(0, 100)}`
        );

    const body = findAll(doc.documentElement, 'body')[0];
    if (!body) throw new Error('Invalid DOCX: no w:body element found');

    const dropped: DroppedContent = {
        images: 0,
        footnotes: 0,
        endnotes: 0,
        objects: 0,
    };
    const ctx: ParserContext = { rels, numbering, headingStyles, dropped };
    const html = processBlockChildren(body, ctx);

    if (dropped.images > 0)
        warnings.push(
            `${dropped.images} image${dropped.images === 1 ? ' was' : 's were'} not imported`
        );
    if (dropped.footnotes > 0)
        warnings.push(
            `${dropped.footnotes} footnote${dropped.footnotes === 1 ? ' was' : 's were'} removed`
        );
    if (dropped.endnotes > 0)
        warnings.push(
            `${dropped.endnotes} endnote${dropped.endnotes === 1 ? ' was' : 's were'} removed`
        );
    if (dropped.objects > 0)
        warnings.push(
            `${dropped.objects} embedded object${dropped.objects === 1 ? ' was' : 's were'} not imported`
        );

    return { html, warnings };
}
